import type { ReactNode } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import type { DealStage } from "../types";

const FALLBACK_COLOR = "#65705f";

export const StageBadge = ({
  stage,
  className,
}: {
  stage: DealStage | undefined;
  className?: string;
}) => {
  const color = stage?.color ?? FALLBACK_COLOR;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
        className,
      )}
      style={{ color, backgroundColor: `${color}1f` }}
    >
      {stage?.label}
    </span>
  );
};

/** Page frame: the mobile header and padding on phones, plain on desktop */
export const FunnelShell = ({
  title,
  actions,
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) => {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="text-xl font-bold truncate">{title}</h1>
        </MobileHeader>
        <MobileContent>
          {actions ? <div className="mb-3 flex gap-2">{actions}</div> : null}
          {children}
        </MobileContent>
      </>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        <h1 className="text-3xl font-extrabold tracking-tight">{title}</h1>
        {actions}
      </div>
      {children}
    </div>
  );
};

/** A native select styled like the inputs: reliable on phones */
export const NativeSelect = ({
  className,
  ...props
}: React.ComponentProps<"select">) => (
  <select
    {...props}
    className={cn(
      "h-9 rounded-lg border border-input bg-card px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
      className,
    )}
  />
);
